import { CircularProgress } from '@mui/material'
import { Button, Modal, pantryColors } from '@pantry/ui'
import React, { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import type {
  CampaignDisplayStatus,
  CampaignStats,
  FlowStatus,
  LoyaltyCampaign,
  LoyaltyFlow,
  UpdateCampaignInput,
} from '../../../../types/loyaltyMarketing'
import {
  displayStatusForCampaign,
  displayStatusForFlow,
} from '../../../../types/loyaltyMarketing'
import { useLoyaltyContext } from '../../hooks/useLoyaltyContext'
import {
  useCreateLoyaltyCampaign,
  useCreateLoyaltyFlow,
  useDeleteLoyaltyCampaign,
  useDeleteLoyaltyFlow,
  useLoyaltyCampaignsQuery,
  useLoyaltyFlowsQuery,
  useSendLoyaltyCampaign,
  useUpdateLoyaltyCampaign,
  useUpdateLoyaltyFlow,
} from '../../hooks/useLoyaltyMarketing'
import { useLoyaltySegmentsQuery } from '../../segments/hooks/useLoyaltySegments'
import {
  type CampaignPerformance,
  type CampaignRevenue,
  type CampaignSegmentFilter,
  type CampaignTypeFilter,
  type DateRange,
  type RateCell,
  SMART_SEND_LABEL,
  type SortKey,
  bulkDeleteSkippedNote,
  campaignAudienceSummary,
  campaignChannel,
  campaignPerformance,
  campaignRevenue,
  campaignType,
  emailRates,
  filterByDate,
  filterCampaigns,
  filterFlows,
  flowListType,
  flowPerformanceSummary,
  formatListDate,
  flowRevenue,
  flowRowDescription,
  isSmartSendCampaign,
  listSummary,
  sortCampaignRows,
} from '../campaignDisplay'
import { campaignsToCsv, downloadCsv } from '../campaignExport'
import { blocksCopyFields } from '../builder/useCampaignDraft'
import {
  EMPTY_PRODUCTS_NOT_SENDABLE_MESSAGE,
  NOT_SENDABLE_MESSAGE,
  storedRecordSendable,
} from '../campaignValidation'
import {
  getMarketingErrorMessage,
  useMarketingToast,
} from '../hooks/useMarketingToast'
import {
  cardStyle,
  inputStyle,
  tdStyle as baseTdStyle,
  thStyle as baseThStyle,
} from '../styles'
import { CampaignDetailDrawer, type DrawerTarget } from './CampaignDetailDrawer'
import {
  CAMPAIGN_TYPE_TINTS,
  CampaignTypeIcon,
  EMERALD,
  flowVisual,
} from './CampaignTypeIcon'
import { CampaignsCalendar } from './CampaignsCalendar'
import { ScheduleCampaignDialog } from './ScheduleCampaignDialog'
import { StatusPill } from './StatusPill'
import { TypeTag } from './TypeTag'

const p = pantryColors

// Prototype table: 11.5px/600 gray-500 uppercase header on white (sticky), 66px rows.
const thStyle: React.CSSProperties = {
  ...baseThStyle,
  fontSize: 11.5,
  fontWeight: 600,
  color: p.gray[500],
  background: p.white,
  position: 'sticky',
  top: 0,
  zIndex: 1,
}
const tdStyle: React.CSSProperties = baseTdStyle

const currencyFormat = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
})

const DATE_OPTIONS = [
  { value: 'all', label: 'All Time' },
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: 'year', label: 'This year' },
  { value: 'custom', label: 'Custom range' },
] as const

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
  { value: 'open_rate', label: 'Highest Open Rate' },
  { value: 'revenue', label: 'Highest Revenue' },
]

// Campaign statuses whose content the server still accepts PATCHes for —
// mirrors the builder's own guard so locked rows open read-only.
const CONTENT_EDITABLE_STATUSES = ['draft', 'scheduled', 'paused']

// The server's own 400 for `type: 'sms'` on create — surfaced by Duplicate
// without a round-trip when the source is a legacy SMS campaign.
const SMS_COMING_SOON = 'SMS campaigns are coming soon'

const formatDate = (value?: string) => formatListDate(value)

// Resuming a paused Smart send time campaign re-enters 'scheduled', which the
// server only accepts with a scheduled_at strictly in the future. This is the
// lead it gets: long enough to survive client/server clock skew, short enough
// that "resumed" means resumed.
const SMART_RESUME_LEAD_MS = 2 * 60 * 1000

// The stored scheduled_at as ISO when it is still ahead of now, else null —
// a paused campaign can only resume onto a future slot.
const futureScheduledAt = (campaign: LoyaltyCampaign): string | null => {
  if (!campaign.scheduled_at) return null
  const stored = new Date(campaign.scheduled_at)
  if (Number.isNaN(stored.getTime()) || stored <= new Date()) return null
  return stored.toISOString()
}

// Reference `.stat-item` — value (22/700) above a plain 11.5px label; an
// optional 8px status dot sits before the value.
const StatItem: React.FC<{
  label: string
  value: string
  color?: string
  dot?: boolean
}> = ({ label, value, color, dot }) => (
  <div
    style={{
      flex: '1 1 0',
      minWidth: 110,
      padding: '14px 20px',
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
    }}
  >
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 7,
        fontSize: 22,
        fontWeight: 700,
        color: color ?? p.gray[800],
      }}
    >
      {dot && (
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: color,
            flexShrink: 0,
          }}
        />
      )}
      {value}
    </div>
    <div style={{ fontSize: 11.5, color: p.gray[400] }}>{label}</div>
  </div>
)

const StatSeparator: React.FC = () => (
  <div
    style={{ width: 1, height: 36, background: p.gray[200], flexShrink: 0 }}
  />
)

// Reference `.perf-row` — one labelled bar of the email performance cell.
// `percent` null renders an em-dash and an empty bar (metric untracked);
// `barPercent` is the bar's fill, always the same rate the label shows.
const PerfBarRow: React.FC<{
  label: string
  cell: RateCell
  barPercent?: number
  color: string
}> = ({ label, cell, barPercent, color }) => {
  const percent = cell.pct
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div
        style={{ fontSize: 10.5, color: p.gray[400], width: 54, flexShrink: 0 }}
      >
        {label}
      </div>
      <div
        style={{
          flex: 1,
          height: 5,
          background: p.gray[100],
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            height: '100%',
            borderRadius: 3,
            background: color,
            width: `${percent === null ? 0 : Math.min(barPercent ?? percent, 100)}%`,
          }}
        />
      </div>
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: p.gray[600],
          minWidth: 78,
          textAlign: 'right',
          flexShrink: 0,
          whiteSpace: 'nowrap',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {cell.label}
      </div>
    </div>
  )
}

// Reference `.perf-auto` — headline count + sub-label (SMS and flow rows).
const PerfSummary: React.FC<{ value: string; sub: string }> = ({
  value,
  sub,
}) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    <div style={{ fontSize: 13, fontWeight: 600, color: p.gray[700] }}>
      {value}
    </div>
    <div style={{ fontSize: 11, color: p.gray[400] }}>{sub}</div>
  </div>
)

// Reference `.perf-sent` — the column before the first send is counted.
const PerfUnsent: React.FC = () => (
  <div style={{ fontSize: 11, color: p.gray[300] }}>Not sent yet</div>
)

const PerformanceCell: React.FC<{
  performance: CampaignPerformance
  stats?: CampaignStats
}> = ({ performance, stats }) => {
  switch (performance.kind) {
    case 'unsent':
      return <PerfUnsent />
    case 'sms':
      return (
        <PerfSummary
          value={`${performance.delivered.toLocaleString()} delivered`}
          sub={`${performance.conversions.toLocaleString()} conversions · ${
            performance.deliveryRate === null
              ? '—'
              : `${performance.deliveryRate}%`
          } delivery rate`}
        />
      )
    default: {
      const rates = emailRates(stats)
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <PerfBarRow
            label="Delivered"
            cell={rates.delivered}
            barPercent={performance.deliveredRate ?? 0}
            color={p.gray[400]}
          />
          <PerfBarRow
            label="Opens"
            cell={rates.opens}
            barPercent={performance.openRate ?? 0}
            color={p.blue[700]}
          />
          <PerfBarRow
            label="Clicks"
            cell={rates.clicks}
            barPercent={performance.clickRate ?? 0}
            color={p.brand.primary}
          />
        </div>
      )
    }
  }
}

// Reference `.date-primary` / `.date-sub` — lifecycle word + timestamp.
const DateCell: React.FC<{ primary: string; sub: string; color?: string }> = ({
  primary,
  sub,
  color,
}) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    <div style={{ fontSize: 13, color: color ?? p.gray[700] }}>{primary}</div>
    <div style={{ fontSize: 11, color: p.gray[400], whiteSpace: 'nowrap' }}>
      {sub}
    </div>
  </div>
)

// Reference `.camp-row` — a clickable table row with the hover wash. A <tr>
// can't be a <button>, so the row carries the click and the trailing cell
// stops propagation for its controls (see `stopRowClick`).
const ListRow: React.FC<{
  onClick: () => void
  title?: string
  selected?: boolean
  children: (hovered: boolean) => React.ReactNode
}> = ({ onClick, title, selected, children }) => {
  const [hovered, setHovered] = useState(false)
  return (
    <tr
      title={title}
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (
          e.target === e.currentTarget &&
          (e.key === 'Enter' || e.key === ' ')
        ) {
          e.preventDefault()
          onClick()
        }
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
      style={{
        cursor: 'pointer',
        height: 66,
        background: selected
          ? p.brand.light
          : hovered
            ? p.gray[50]
            : 'transparent',
        transition: 'background .12s',
      }}
    >
      {children(hovered)}
    </tr>
  )
}

// Mockup `.cb-col` checkbox — swallows the click so the row doesn't open.
const RowCheckbox: React.FC<{
  checked: boolean
  label: string
  onToggle: () => void
}> = ({ checked, label, onToggle }) => (
  <input
    type="checkbox"
    aria-label={`Select ${label}`}
    checked={checked}
    onChange={onToggle}
    onClick={(e) => e.stopPropagation()}
    onKeyDown={(e) => e.stopPropagation()}
    style={{ cursor: 'pointer', margin: 0, accentColor: p.brand.primary }}
  />
)

// Mockup `.row-btn` — 28px icon button, hover-revealed with the row.
const RowActionButton: React.FC<{
  title: string
  disabled?: boolean
  onClick: () => void
  children: React.ReactNode
}> = ({ title, disabled, onClick, children }) => (
  <button
    type="button"
    title={title}
    aria-label={title}
    disabled={disabled}
    onClick={onClick}
    onMouseEnter={(e) => {
      e.currentTarget.style.background = p.gray[200]
      e.currentTarget.style.color = p.gray[700]
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = 'transparent'
      e.currentTarget.style.color = p.gray[400]
    }}
    style={{
      width: 28,
      height: 28,
      display: 'grid',
      placeItems: 'center',
      border: 'none',
      borderRadius: 6,
      background: 'transparent',
      color: p.gray[400],
      cursor: disabled ? 'default' : 'pointer',
      padding: 0,
      opacity: disabled ? 0.5 : 1,
    }}
  >
    {children}
  </button>
)

const EyeIcon: React.FC = () => (
  <svg
    width={14}
    height={14}
    fill="none"
    stroke="currentColor"
    strokeWidth={1.7}
    viewBox="0 0 24 24"
    aria-hidden
  >
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
)

const PencilIcon: React.FC = () => (
  <svg
    width={14}
    height={14}
    fill="none"
    stroke="currentColor"
    strokeWidth={1.7}
    viewBox="0 0 24 24"
    aria-hidden
  >
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
)

const CopyIcon: React.FC = () => (
  <svg
    width={14}
    height={14}
    fill="none"
    stroke="currentColor"
    strokeWidth={1.7}
    viewBox="0 0 24 24"
    aria-hidden
  >
    <rect x="9" y="9" width="13" height="13" rx="2" />
    <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
  </svg>
)

// View / Edit / Copy, visible on row hover (mockup `tr:hover .row-actions`).
const RowActions: React.FC<{
  visible: boolean
  busy: boolean
  onView: () => void
  onEdit: () => void
  onCopy: () => void
}> = ({ visible, busy, onView, onEdit, onCopy }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 4,
      opacity: visible ? 1 : 0,
      transition: 'opacity .15s',
    }}
  >
    <RowActionButton title="View" onClick={onView}>
      <EyeIcon />
    </RowActionButton>
    <RowActionButton title="Edit" onClick={onEdit}>
      <PencilIcon />
    </RowActionButton>
    <RowActionButton title="Duplicate" disabled={busy} onClick={onCopy}>
      <CopyIcon />
    </RowActionButton>
  </div>
)

// Reference `.camp-cell` — tinted icon square + name + one-line description.
const CampaignCell: React.FC<{
  icon: React.ReactNode
  tint: { fg: string; bg: string }
  name: string
  description: string
  auto?: boolean
}> = ({ icon, tint, name, description, auto }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
    <div
      style={{
        width: 38,
        height: 38,
        borderRadius: 10,
        display: 'grid',
        placeItems: 'center',
        flexShrink: 0,
        background: tint.bg,
        color: tint.fg,
      }}
    >
      {icon}
    </div>
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          minWidth: 0,
        }}
      >
        <span
          style={{
            fontSize: 13.5,
            fontWeight: 600,
            color: p.gray[800],
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: 260,
          }}
        >
          {name}
        </span>
        {auto && (
          <span
            style={{
              fontSize: 10.5,
              fontWeight: 700,
              padding: '2px 8px',
              borderRadius: 20,
              background: p.purple[50],
              color: p.purple[700],
              letterSpacing: '0.03em',
              flexShrink: 0,
            }}
          >
            AUTO
          </span>
        )}
      </div>
      {description && (
        <div
          style={{
            fontSize: 11.5,
            color: p.gray[400],
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: 220,
          }}
        >
          {description}
        </div>
      )}
    </div>
  </div>
)

// Reference `.rev-val` / `.rev-sub` — attributed revenue over the 7-day
// post-send window with the conversion count; "—" when nothing can be
// attributed (unsent, or sent before the recipient ledger existed).
const RevenueCell: React.FC<{ revenue: CampaignRevenue }> = ({ revenue }) => {
  if (revenue.kind === 'unknown' || revenue.revenue <= 0) {
    return (
      <div style={{ fontSize: 13, color: p.gray[300], textAlign: 'right' }}>
        —
      </div>
    )
  }
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        textAlign: 'right',
      }}
      title={
        revenue.final
          ? 'Orders by recipients within 7 days of the send'
          : 'Orders by recipients so far — the 7-day window is still open'
      }
    >
      <div
        style={{
          fontSize: 13.5,
          fontWeight: 700,
          color: p.gray[800],
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {currencyFormat.format(revenue.revenue)}
      </div>
      <div style={{ fontSize: 11, color: p.gray[400] }}>
        {revenue.conversions.toLocaleString()} conv.
        {revenue.conversionPct !== null && ` · ${revenue.conversionPct}%`}
      </div>
    </div>
  )
}

// Reference `.audience-cell` — segment name + optional size sub-line.
const AudienceCell: React.FC<{ label: string; size?: string }> = ({
  label,
  size,
}) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    <div style={{ fontSize: 13, fontWeight: 500, color: p.gray[700] }}>
      {label}
    </div>
    {size && <div style={{ fontSize: 11, color: p.gray[400] }}>{size}</div>}
  </div>
)

const TYPE_FILTER_OPTIONS: { value: CampaignTypeFilter; label: string }[] = [
  { value: 'all', label: 'All Types' },
  { value: 'email', label: 'Email' },
  { value: 'sms', label: 'SMS' },
  { value: 'abandoned_cart', label: 'Abandoned Cart' },
  { value: 'flows', label: 'Always-on flows' },
]

const STATUS_FILTER_OPTIONS: {
  value: 'all' | CampaignDisplayStatus
  label: string
}[] = [
  { value: 'all', label: 'All Statuses' },
  { value: 'running', label: 'Active' },
  { value: 'preparing', label: 'Preparing' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'draft', label: 'Draft' },
  { value: 'paused', label: 'Paused' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
]

const DownloadIcon: React.FC = () => (
  <svg
    width={14}
    height={14}
    fill="none"
    stroke="currentColor"
    strokeWidth={1.7}
    viewBox="0 0 24 24"
    aria-hidden
  >
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
  </svg>
)

const PlusIcon: React.FC = () => (
  <svg
    width={14}
    height={14}
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    viewBox="0 0 24 24"
    aria-hidden
  >
    <path d="M12 5v14M5 12h14" />
  </svg>
)

const SearchIcon: React.FC = () => (
  <svg
    width={14}
    height={14}
    fill="none"
    stroke="currentColor"
    strokeWidth={1.7}
    viewBox="0 0 24 24"
    aria-hidden
  >
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
)

const ListViewIcon: React.FC = () => (
  <svg
    width={14}
    height={14}
    fill="none"
    stroke="currentColor"
    strokeWidth={1.7}
    viewBox="0 0 24 24"
    aria-hidden
  >
    <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
  </svg>
)

const CalendarViewIcon: React.FC = () => (
  <svg
    width={14}
    height={14}
    fill="none"
    stroke="currentColor"
    strokeWidth={1.7}
    viewBox="0 0 24 24"
    aria-hidden
  >
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M16 2v4M8 2v4M3 10h18" />
  </svg>
)

type CampaignsView = 'list' | 'calendar'

// draft→Schedule opens the picker; paused→Resume reuses the stored
// scheduled_at when it is still in the future, otherwise asks for a new one.
interface ScheduleDialogTarget {
  campaign: LoyaltyCampaign
  mode: 'schedule' | 'resume'
}

// Merged list rows — one-off campaigns and always-on flows share the table.
type ListRow =
  | { kind: 'campaign'; key: string; sortAt: number; campaign: LoyaltyCampaign }
  | { kind: 'flow'; key: string; sortAt: number; flow: LoyaltyFlow }

const timestamp = (value?: string): number => {
  if (!value) return 0
  const parsed = new Date(value).getTime()
  return Number.isNaN(parsed) ? 0 : parsed
}

export const CampaignsTab: React.FC = () => {
  const { storeId } = useLoyaltyContext()
  const { storeId: routeStoreId } = useParams<{ storeId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { showToast, toastNode } = useMarketingToast()

  // The builder hands a confirmation over via navigate(backPath, { state:
  // { toast } }) — show it once, then clear it so a refresh/back doesn't repeat.
  const handoffToast = (location.state as { toast?: unknown } | null)?.toast
  useEffect(() => {
    if (typeof handoffToast !== 'string' || !handoffToast) return
    showToast(handoffToast)
    navigate(location.pathname + location.search, {
      replace: true,
      state: null,
    })
  }, [handoffToast, showToast, navigate, location.pathname, location.search])

  const {
    data: campaigns,
    isLoading,
    isFetching,
  } = useLoyaltyCampaignsQuery(storeId)
  const { data: segments } = useLoyaltySegmentsQuery(storeId)
  const {
    data: flows,
    isLoading: flowsLoading,
    isFetching: flowsFetching,
  } = useLoyaltyFlowsQuery(storeId)

  const [view, setView] = useState<CampaignsView>('list')
  const [deleteTarget, setDeleteTarget] = useState<ListRow | null>(null)
  // Bulk selection (mockup checkbox column + "N selected · Duplicate ·
  // Delete" bar). Keys are ListRow.key so campaigns and flows mix freely.
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set())
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false)
  const [bulkBusy, setBulkBusy] = useState(false)
  const createMutation = useCreateLoyaltyCampaign(storeId)
  const createFlowMutation = useCreateLoyaltyFlow(storeId)
  // Detail drawer target — stored as {kind,id} and resolved against the live
  // lists so a Pause/Resume from the footer re-renders with the new status.
  const [drawerTarget, setDrawerTarget] = useState<{
    kind: 'campaign' | 'flow'
    id: string
  } | null>(null)
  const [scheduleTarget, setScheduleTarget] =
    useState<ScheduleDialogTarget | null>(null)
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState<CampaignTypeFilter>('all')
  const [segmentFilter, setSegmentFilter] =
    useState<CampaignSegmentFilter>('all')
  const [statusFilter, setStatusFilter] = useState<
    'all' | CampaignDisplayStatus
  >('all')
  const [datePreset, setDatePreset] = useState<
    'all' | '7' | '30' | '90' | 'year' | 'custom'
  >('all')
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')
  const [sortKey, setSortKey] = useState<SortKey>('newest')
  const dateRange: DateRange =
    datePreset === 'custom' ? { from: customFrom, to: customTo } : datePreset

  const sendMutation = useSendLoyaltyCampaign(storeId)
  const updateMutation = useUpdateLoyaltyCampaign(storeId)
  const deleteMutation = useDeleteLoyaltyCampaign(storeId)
  const updateFlowMutation = useUpdateLoyaltyFlow(storeId)
  const deleteFlowMutation = useDeleteLoyaltyFlow(storeId)

  // Send / schedule / set live from the list act on the stored record: an
  // unfinished block email must go back through the builder first.
  const guardSendable = (record: LoyaltyCampaign | LoyaltyFlow): boolean => {
    const sendable = storedRecordSendable(record)
    if (sendable.ok) return true
    showToast(
      sendable.reason === 'empty_product_blocks'
        ? EMPTY_PRODUCTS_NOT_SENDABLE_MESSAGE
        : NOT_SENDABLE_MESSAGE,
      'error',
    )
    return false
  }

  const handleSend = async (campaign: LoyaltyCampaign) => {
    if (!guardSendable(campaign)) return
    try {
      await sendMutation.mutateAsync(campaign.id)
      showToast('Campaign is sending')
    } catch (error) {
      showToast(getMarketingErrorMessage(error), 'error')
    }
  }

  const handleTransition = async (
    campaign: LoyaltyCampaign,
    input: UpdateCampaignInput,
    successMessage: string,
  ) => {
    try {
      await updateMutation.mutateAsync({ campaignId: campaign.id, input })
      showToast(successMessage)
      setScheduleTarget(null)
    } catch (error) {
      showToast(getMarketingErrorMessage(error), 'error')
    }
  }

  const handleFlowTransition = async (
    flow: LoyaltyFlow,
    status: FlowStatus,
    successMessage: string,
  ) => {
    try {
      await updateFlowMutation.mutateAsync({
        flowId: flow.id,
        input: { status },
      })
      showToast(successMessage)
    } catch (error) {
      showToast(getMarketingErrorMessage(error), 'error')
    }
  }

  // paused → scheduled: reuse the stored scheduled_at while it is still in
  // the future, otherwise ask for a new one. The single rule behind the
  // drawer footer's Resume.
  const resumeCampaign = (campaign: LoyaltyCampaign) => {
    // A Smart send time campaign has no fixed send time to resume onto — the
    // server re-plans the 24h spread at launch and skips everyone it already
    // served. Asking the operator for a date would be a lie (the list would
    // then show a concrete time the drawer still calls "Smart send time"), so
    // resume just relaunches the smart plan. The transition into 'scheduled'
    // still needs a future timestamp, hence the small lead.
    if (isSmartSendCampaign(campaign)) {
      handleTransition(
        campaign,
        {
          status: 'scheduled',
          scheduled_at: new Date(
            Date.now() + SMART_RESUME_LEAD_MS,
          ).toISOString(),
        },
        'Smart send resumed — remaining recipients will be re-planned',
      )
      return
    }
    const future = futureScheduledAt(campaign)
    if (future) {
      handleTransition(
        campaign,
        { status: 'scheduled', scheduled_at: future },
        'Campaign resumed',
      )
      return
    }
    // SlideDrawer and Modal both listen for Escape on the document, so the
    // drawer must be gone before the schedule dialog stacks on top of it.
    setDrawerTarget(null)
    setScheduleTarget({ campaign, mode: 'resume' })
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    try {
      if (deleteTarget.kind === 'flow') {
        await deleteFlowMutation.mutateAsync(deleteTarget.flow.id)
        showToast('Flow deleted')
      } else {
        await deleteMutation.mutateAsync(deleteTarget.campaign.id)
        showToast('Campaign deleted')
      }
      setDeleteTarget(null)
    } catch (error) {
      showToast(getMarketingErrorMessage(error), 'error')
    }
  }

  const campaignList = campaigns ?? []
  const flowList = flows ?? []

  const drawerRecord = useMemo<DrawerTarget | null>(() => {
    if (!drawerTarget) return null
    if (drawerTarget.kind === 'campaign') {
      const campaign = campaignList.find((c) => c.id === drawerTarget.id)
      return campaign ? { kind: 'campaign', campaign } : null
    }
    const flow = flowList.find((f) => f.id === drawerTarget.id)
    return flow ? { kind: 'flow', flow } : null
  }, [drawerTarget, campaignList, flowList])

  const summary = listSummary(campaignList, flowList)

  // Merged, newest-first list. Type/segment bucketing lives in
  // filterCampaigns / filterFlows (campaignDisplay.ts).
  const rows = useMemo<ListRow[]>(() => {
    const campaignRows: ListRow[] = filterByDate(
      filterCampaigns(campaignList, {
        search,
        type: typeFilter,
        status: statusFilter,
        segment: segmentFilter,
      }),
      dateRange,
    ).map((campaign) => ({
      kind: 'campaign',
      key: `campaign-${campaign.id}`,
      sortAt: timestamp(
        campaign.sent_at || campaign.scheduled_at || campaign.created_at,
      ),
      campaign,
    }))
    // Flows carry no send/schedule time; their creation date stands in.
    const flowRows: ListRow[] = filterByDate(
      filterFlows(flowList, {
        search,
        status: statusFilter,
        type: typeFilter,
        segment: segmentFilter,
      }).map((flow) => ({ flow, scheduled_at: flow.created_at })),
      dateRange,
    ).map(({ flow }) => ({
      kind: 'flow',
      key: `flow-${flow.id}`,
      sortAt: timestamp(flow.created_at),
      flow,
    }))
    return sortCampaignRows([...campaignRows, ...flowRows], sortKey)
  }, [
    campaignList,
    flowList,
    search,
    typeFilter,
    statusFilter,
    segmentFilter,
    datePreset,
    customFrom,
    customTo,
    sortKey,
  ])

  const selectedRows = rows.filter((row) => selectedKeys.has(row.key))
  // Export: the selection when one exists, else everything the filters show.
  const handleExport = () => {
    const source = selectedRows.length > 0 ? selectedRows : rows
    const segmentsById = Object.fromEntries(
      (segments ?? []).map((segment) => [segment.id, segment]),
    )
    downloadCsv(
      campaignsToCsv(
        source.map((row) =>
          row.kind === 'campaign' ? row.campaign : row.flow,
        ),
        segmentsById,
      ),
    )
  }

  const toggleSelected = (key: string) =>
    setSelectedKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  const allVisibleSelected =
    rows.length > 0 && rows.every((row) => selectedKeys.has(row.key))
  const toggleAllVisible = () =>
    setSelectedKeys(
      allVisibleSelected ? new Set() : new Set(rows.map((row) => row.key)),
    )

  // Duplicate: a draft copy with the same v2 content and audience
  // ("<name> (copy)"). Sends/schedules are never copied; neither are the
  // retired template_id / field_values / reward. Shared by the row Copy
  // button, the drawer footer and the bulk bar. Returns false for an SMS
  // campaign or SMS flow — SMS sending is not shipped (the server rejects
  // `type: 'sms'` on create).
  // Legacy records serialise unset content as null (from_email / from_label
  // always; older docs for the rest). The create serializers' content fields
  // are `required=False, allow_blank=True` but NOT `allow_null` — only
  // logo_url is — so null must be omitted, not forwarded.
  const orOmit = <T,>(value: T | null | undefined): T | undefined =>
    value ?? undefined
  const duplicateRow = async (row: ListRow): Promise<boolean> => {
    if (row.kind === 'flow') {
      const f = row.flow
      if (flowListType(f) === 'sms') return false
      await createFlowMutation.mutateAsync({
        name: `${f.name || 'Untitled Flow'} (copy)`,
        trigger_metric: f.trigger_metric,
        delay_value: f.delay_value,
        delay_unit: f.delay_unit,
        min_cart_value: orOmit(f.min_cart_value),
        channel: orOmit(f.channel),
        sms_body: orOmit(f.sms_body),
        subject: f.subject ?? '',
        preview_text: orOmit(f.preview_text),
        body_html: orOmit(f.body_html),
        cta_text: orOmit(f.cta_text),
        cta_url: orOmit(f.cta_url),
        logo_url: f.logo_url ?? null,
        coupon_code: orOmit(f.coupon_code),
        from_email: orOmit(f.from_email),
        from_label: orOmit(f.from_label),
        ...blocksCopyFields(f),
        status: 'draft',
      })
      return true
    }
    const c = row.campaign
    const type = campaignType(c)
    if (type === 'sms') return false
    await createMutation.mutateAsync({
      name: `${c.name || 'Untitled Campaign'} (copy)`,
      type,
      channel: orOmit(c.channel),
      subject: c.subject ?? '',
      preview_text: orOmit(c.preview_text),
      body_html: orOmit(c.body_html),
      cta_text: orOmit(c.cta_text),
      cta_url: orOmit(c.cta_url),
      logo_url: c.logo_url ?? null,
      coupon_code: orOmit(c.coupon_code),
      from_email: orOmit(c.from_email),
      from_label: orOmit(c.from_label),
      ...blocksCopyFields(c),
      audience: c.audience,
      ends_at: c.ends_at ?? null,
      send: 'draft',
    })
    return true
  }

  const handleDuplicateOne = async (row: ListRow) => {
    setBulkBusy(true)
    try {
      const copied = await duplicateRow(row)
      if (!copied) {
        showToast(SMS_COMING_SOON, 'error')
        return
      }
      showToast(
        row.kind === 'flow'
          ? 'Automation duplicated as a draft'
          : 'Campaign duplicated as a draft',
      )
    } catch (error) {
      showToast(getMarketingErrorMessage(error), 'error')
    } finally {
      setBulkBusy(false)
    }
  }

  const handleBulkDuplicate = async () => {
    if (selectedRows.length === 0) return
    setBulkBusy(true)
    let copied = 0
    let skipped = 0
    try {
      for (const row of selectedRows) {
        if (await duplicateRow(row)) copied += 1
        else skipped += 1
      }
      if (copied > 0) {
        showToast(
          `Duplicated ${copied} ${copied === 1 ? 'item' : 'items'} as drafts${
            skipped > 0 ? ` · ${skipped} SMS skipped` : ''
          }`,
        )
      } else {
        showToast(SMS_COMING_SOON, 'error')
      }
      setSelectedKeys(new Set())
    } catch (error) {
      showToast(getMarketingErrorMessage(error), 'error')
    } finally {
      setBulkBusy(false)
    }
  }

  // Bulk delete skips rows the server would refuse (an in-flight send).
  const bulkDeletable = selectedRows.filter(
    (row) =>
      row.kind === 'flow' ||
      (row.campaign.status !== 'sending' &&
        row.campaign.status !== 'preparing'),
  )
  const handleBulkDelete = async () => {
    if (bulkDeletable.length === 0) return
    setBulkBusy(true)
    let removed = 0
    try {
      for (const row of bulkDeletable) {
        if (row.kind === 'flow')
          await deleteFlowMutation.mutateAsync(row.flow.id)
        else await deleteMutation.mutateAsync(row.campaign.id)
        removed += 1
      }
      showToast(`Deleted ${removed} ${removed === 1 ? 'item' : 'items'}`)
      setSelectedKeys(new Set())
      setBulkDeleteOpen(false)
    } catch (error) {
      showToast(getMarketingErrorMessage(error), 'error')
    } finally {
      setBulkBusy(false)
    }
  }

  const segmentById = (segmentId: string) =>
    segments?.find((s) => s.id === segmentId)

  const listLoading = isLoading || flowsLoading

  const openCampaign = (campaign: LoyaltyCampaign) =>
    navigate(`/s/${routeStoreId}/loyalty/campaigns/builder/${campaign.id}`)
  const openFlow = (flow: LoyaltyFlow) =>
    navigate(`/s/${routeStoreId}/loyalty/campaigns/builder/flow/${flow.id}`)
  const viewRow = (row: ListRow) =>
    setDrawerTarget({
      kind: row.kind,
      id: row.kind === 'flow' ? row.flow.id : row.campaign.id,
    })
  const editTarget = (target: DrawerTarget) => {
    setDrawerTarget(null)
    if (target.kind === 'flow') openFlow(target.flow)
    else openCampaign(target.campaign)
  }
  const targetToRow = (target: DrawerTarget): ListRow =>
    target.kind === 'flow'
      ? {
          kind: 'flow',
          key: `flow-${target.flow.id}`,
          sortAt: 0,
          flow: target.flow,
        }
      : {
          kind: 'campaign',
          key: `campaign-${target.campaign.id}`,
          sortAt: 0,
          campaign: target.campaign,
        }

  // Drawer footer status actions — the only per-record controls now that the
  // rows carry just View / Edit / Copy. Pause / Resume (flows: Set live) reuse
  // the same transitions; Send now, Schedule…, Back to draft and Delete are
  // campaign/flow-aware wrappers around the existing handlers.
  const pauseTarget = (target: DrawerTarget) => {
    if (target.kind === 'flow')
      handleFlowTransition(target.flow, 'paused', 'Flow paused')
    else
      handleTransition(target.campaign, { status: 'paused' }, 'Campaign paused')
  }
  const resumeTarget = (target: DrawerTarget) => {
    if (target.kind === 'flow') {
      if (!guardSendable(target.flow)) return
      handleFlowTransition(target.flow, 'live', 'Flow is live')
    } else resumeCampaign(target.campaign)
  }
  const sendTarget = (target: DrawerTarget) => {
    if (target.kind === 'campaign') handleSend(target.campaign)
  }
  // Drawer → schedule dialog and drawer → delete confirm both stack a Modal
  // over the SlideDrawer, and both listen for Escape on the document, so the
  // drawer is closed first (same rule as `resumeCampaign`).
  const scheduleFromDrawer = (target: DrawerTarget) => {
    if (target.kind !== 'campaign') return
    if (!guardSendable(target.campaign)) return
    setDrawerTarget(null)
    setScheduleTarget({ campaign: target.campaign, mode: 'schedule' })
  }
  const backToDraftTarget = (target: DrawerTarget) => {
    if (target.kind === 'flow')
      handleFlowTransition(target.flow, 'draft', 'Flow set to draft')
    else
      handleTransition(
        target.campaign,
        { status: 'draft' },
        'Campaign reverted to draft',
      )
  }
  const deleteFromDrawer = (target: DrawerTarget) => {
    setDrawerTarget(null)
    setDeleteTarget(targetToRow(target))
  }

  const goToBuilder = () =>
    navigate(`/s/${routeStoreId}/loyalty/campaigns/builder`)

  // Row action buttons live inside a clickable row — swallow the click so the
  // action never also opens the drawer.
  const stopRowClick = (event: React.MouseEvent) => event.stopPropagation()

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            background: p.white,
            border: `1px solid ${p.gray[200]}`,
            borderRadius: 10,
          }}
        >
          <StatItem
            label="Total Campaigns"
            value={summary.total.toLocaleString()}
          />
          <StatSeparator />
          <StatItem
            label="Active"
            value={summary.active.toLocaleString()}
            color={EMERALD}
            dot
          />
          <StatSeparator />
          <StatItem
            label="Scheduled"
            value={summary.scheduled.toLocaleString()}
            color={p.blue[700]}
            dot
          />
          <StatSeparator />
          <StatItem
            label="Drafts"
            value={summary.drafts.toLocaleString()}
            color={p.amber[700]}
            dot
          />
          <StatSeparator />
          <StatItem
            label="Total Reach"
            value={summary.reach.toLocaleString()}
          />
          <StatSeparator />
          <StatItem
            label="Avg Open Rate"
            value={
              summary.avgOpenRate === null ? '—' : `${summary.avgOpenRate}%`
            }
          />
          <StatSeparator />
          <StatItem
            label="Revenue Attributed"
            value={currencyFormat.format(summary.revenue)}
            color={EMERALD}
          />
        </div>

        <div style={cardStyle}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px 16px 14px',
              borderBottom: `1px solid ${p.gray[100]}`,
            }}
          >
            <div>
              <div
                style={{ fontSize: 15, fontWeight: 700, color: p.gray[800] }}
              >
                Campaigns
              </div>
              <div style={{ fontSize: 12.5, color: p.gray[500], marginTop: 2 }}>
                One-off sends and always-on automations. Select a row to view
                its details.
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {(isFetching || flowsFetching) && (
                <CircularProgress
                  size={16}
                  thickness={5}
                  sx={{ color: p.brand.primary }}
                />
              )}
              <Button
                variant="ghost"
                disabled={rows.length === 0}
                icon={<DownloadIcon />}
                onClick={handleExport}
              >
                {selectedRows.length > 0
                  ? `Export ${selectedRows.length} selected`
                  : 'Export'}
              </Button>
              <Button
                variant="primary"
                disabled={!storeId}
                icon={<PlusIcon />}
                onClick={goToBuilder}
              >
                Create Campaign
              </Button>
            </div>
          </div>

          {/* List | Calendar view tabs (reference `.view-tab` styling). */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '0 20px',
              borderBottom: `1px solid ${p.gray[100]}`,
            }}
          >
            {(
              [
                { key: 'list', label: 'List View', icon: <ListViewIcon /> },
                {
                  key: 'calendar',
                  label: 'Calendar View',
                  icon: <CalendarViewIcon />,
                },
              ] as {
                key: CampaignsView
                label: string
                icon: React.ReactNode
              }[]
            ).map((tab) => {
              const active = view === tab.key
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setView(tab.key)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 7,
                    padding: '11px 14px',
                    fontSize: 13,
                    fontWeight: active ? 600 : 500,
                    color: active ? p.brand.primary : p.gray[500],
                    border: 'none',
                    borderBottom: `2px solid ${
                      active ? p.brand.primary : 'transparent'
                    }`,
                    marginBottom: -1,
                    background: 'transparent',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {tab.icon}
                  {tab.label}
                </button>
              )
            })}
          </div>

          {view === 'calendar' ? (
            <CampaignsCalendar
              campaigns={campaignList}
              flows={flowList}
              onOpen={(kind, id) => setDrawerTarget({ kind, id })}
            />
          ) : (
            <>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '14px 20px',
                  borderBottom: `1px solid ${p.gray[100]}`,
                  flexWrap: 'wrap',
                }}
              >
                <div
                  style={{
                    position: 'relative',
                    flex: '0 1 240px',
                    minWidth: 160,
                    color: p.gray[400],
                  }}
                >
                  <span
                    style={{
                      position: 'absolute',
                      left: 10,
                      top: 0,
                      bottom: 0,
                      display: 'grid',
                      placeItems: 'center',
                      pointerEvents: 'none',
                    }}
                  >
                    <SearchIcon />
                  </span>
                  <input
                    aria-label="Search campaigns"
                    style={{
                      ...inputStyle,
                      background: p.gray[50],
                      paddingLeft: 32,
                    }}
                    type="text"
                    placeholder="Search campaigns…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                <select
                  style={{ ...inputStyle, width: 'auto', color: p.gray[600] }}
                  value={typeFilter}
                  onChange={(e) =>
                    setTypeFilter(e.target.value as CampaignTypeFilter)
                  }
                >
                  {TYPE_FILTER_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <select
                  style={{ ...inputStyle, width: 'auto', color: p.gray[600] }}
                  value={statusFilter}
                  onChange={(e) =>
                    setStatusFilter(
                      e.target.value as 'all' | CampaignDisplayStatus,
                    )
                  }
                >
                  {STATUS_FILTER_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <select
                  style={{ ...inputStyle, width: 'auto', color: p.gray[600] }}
                  value={segmentFilter}
                  onChange={(e) => setSegmentFilter(e.target.value)}
                >
                  <option value="all">All Segments</option>
                  <option value="all-customers">All Customers</option>
                  {(segments ?? []).map((segment) => (
                    <option key={segment.id} value={segment.id}>
                      {segment.name}
                    </option>
                  ))}
                </select>
                <div
                  style={{
                    width: 1,
                    height: 24,
                    background: p.gray[200],
                    flexShrink: 0,
                  }}
                />
                <select
                  aria-label="Date range"
                  style={{ ...inputStyle, width: 'auto', color: p.gray[600] }}
                  value={datePreset}
                  onChange={(e) =>
                    setDatePreset(e.target.value as typeof datePreset)
                  }
                >
                  {DATE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {datePreset === 'custom' && (
                  <>
                    <input
                      aria-label="From date"
                      type="date"
                      style={{
                        ...inputStyle,
                        width: 'auto',
                        color: p.gray[600],
                      }}
                      value={customFrom}
                      onChange={(e) => setCustomFrom(e.target.value)}
                    />
                    <span style={{ color: p.gray[400], fontSize: 12.5 }}>
                      →
                    </span>
                    <input
                      aria-label="To date"
                      type="date"
                      style={{
                        ...inputStyle,
                        width: 'auto',
                        color: p.gray[600],
                      }}
                      value={customTo}
                      onChange={(e) => setCustomTo(e.target.value)}
                    />
                  </>
                )}
                {/* Mockup `.bulk-bar`: appears once something is checked. */}
                {selectedRows.length > 0 && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      fontSize: 12.5,
                      color: p.gray[600],
                      background: p.brand.light,
                      borderRadius: 8,
                      padding: '4px 10px',
                    }}
                  >
                    <span style={{ fontWeight: 700, color: p.brand.primary }}>
                      {selectedRows.length} selected
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={bulkBusy}
                      onClick={handleBulkDuplicate}
                    >
                      Duplicate
                    </Button>
                    <Button
                      variant="danger-ghost"
                      size="sm"
                      disabled={bulkBusy || bulkDeletable.length === 0}
                      onClick={() => setBulkDeleteOpen(true)}
                    >
                      Delete
                    </Button>
                    <button
                      type="button"
                      onClick={() => setSelectedKeys(new Set())}
                      style={{
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        color: p.gray[400],
                        fontSize: 12,
                      }}
                    >
                      Clear
                    </button>
                  </div>
                )}
                <select
                  aria-label="Sort"
                  style={{
                    ...inputStyle,
                    width: 'auto',
                    minWidth: 160,
                    marginLeft: 'auto',
                    color: p.gray[600],
                  }}
                  value={sortKey}
                  onChange={(e) => setSortKey(e.target.value as SortKey)}
                >
                  {SORT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {listLoading ? (
                <div
                  style={{
                    padding: '40px 24px',
                    textAlign: 'center',
                    color: p.gray[500],
                  }}
                >
                  <CircularProgress
                    size={20}
                    thickness={5}
                    sx={{ color: p.brand.primary }}
                  />
                  <div style={{ marginTop: 12, fontSize: 13 }}>
                    Loading campaigns…
                  </div>
                </div>
              ) : rows.length === 0 ? (
                <div style={{ padding: '56px 24px', textAlign: 'center' }}>
                  <svg
                    width={52}
                    height={52}
                    fill="none"
                    stroke={p.gray[800]}
                    strokeWidth={1.4}
                    viewBox="0 0 24 24"
                    style={{ opacity: 0.25, marginBottom: 12 }}
                    aria-hidden
                  >
                    <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <div
                    style={{
                      fontSize: 15,
                      fontWeight: 600,
                      color: p.gray[700],
                      marginBottom: 6,
                    }}
                  >
                    No campaigns found
                  </div>
                  <div style={{ fontSize: 13, color: p.gray[500] }}>
                    Try adjusting your filters or{' '}
                    <a
                      href={`/s/${routeStoreId}/loyalty/campaigns/builder`}
                      onClick={(e) => {
                        e.preventDefault()
                        goToBuilder()
                      }}
                      style={{
                        color: p.brand.primary,
                        fontWeight: 600,
                        textDecoration: 'none',
                      }}
                    >
                      create your first campaign
                    </a>
                    .
                  </div>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  {/* Reference `.camp-table` — header row + one <tr> per
                      campaign/flow. Rows are clickable (open the detail
                      drawer); the trailing cell swallows clicks for the
                      controls. */}
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ ...thStyle, paddingLeft: 20, width: 1 }}>
                          <input
                            type="checkbox"
                            aria-label="Select all"
                            checked={allVisibleSelected}
                            onChange={toggleAllVisible}
                            style={{
                              cursor: 'pointer',
                              margin: 0,
                              accentColor: p.brand.primary,
                            }}
                          />
                        </th>
                        <th style={thStyle}>Campaign</th>
                        <th style={thStyle}>Type</th>
                        <th style={thStyle}>Status</th>
                        <th style={thStyle}>Segment</th>
                        <th style={{ ...thStyle, minWidth: 220 }}>
                          Performance
                        </th>
                        <th style={thStyle}>Date</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>
                          Revenue
                        </th>
                        <th style={{ ...thStyle, paddingRight: 20 }} />
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => {
                        if (row.kind === 'flow') {
                          const flow = row.flow
                          const flowStatus = displayStatusForFlow(flow)
                          const listType = flowListType(flow)
                          const visual = flowVisual(
                            listType,
                            flow.trigger_metric,
                          )
                          const flowPerf = flowPerformanceSummary(
                            flow.stats,
                            flow.channel ?? 'email',
                          )
                          return (
                            <ListRow
                              key={row.key}
                              onClick={() => viewRow(row)}
                              selected={selectedKeys.has(row.key)}
                            >
                              {(hovered) => (
                                <>
                                  <td
                                    style={{
                                      ...tdStyle,
                                      paddingLeft: 20,
                                      width: 1,
                                    }}
                                  >
                                    <RowCheckbox
                                      checked={selectedKeys.has(row.key)}
                                      label={flow.name || 'Flow'}
                                      onToggle={() => toggleSelected(row.key)}
                                    />
                                  </td>
                                  <td style={tdStyle}>
                                    <CampaignCell
                                      icon={visual.icon}
                                      tint={visual.tint}
                                      name={flow.name || '—'}
                                      description={flowRowDescription(flow)}
                                      auto
                                    />
                                  </td>
                                  <td style={tdStyle}>
                                    <TypeTag type={listType} />
                                  </td>
                                  <td style={tdStyle}>
                                    <StatusPill status={flowStatus} />
                                  </td>
                                  <td style={tdStyle}>
                                    <AudienceCell
                                      label="All Customers"
                                      size="Auto-targeted"
                                    />
                                  </td>
                                  <td style={tdStyle}>
                                    {flowPerf ? (
                                      <PerfSummary
                                        value={flowPerf.value}
                                        sub={flowPerf.sub}
                                      />
                                    ) : (
                                      <div
                                        style={{
                                          fontSize: 11,
                                          color: p.gray[300],
                                        }}
                                      >
                                        Not triggered yet
                                      </div>
                                    )}
                                  </td>
                                  <td style={tdStyle}>
                                    {flowStatus === 'running' ? (
                                      <DateCell
                                        primary="Active"
                                        sub={`Since ${formatDate(flow.created_at)}`}
                                        color={p.brand.primary}
                                      />
                                    ) : flowStatus === 'paused' ? (
                                      <DateCell
                                        primary="Paused"
                                        sub={`Created ${formatDate(flow.created_at)}`}
                                      />
                                    ) : (
                                      <DateCell
                                        primary="Draft"
                                        sub="Not live"
                                        color={p.gray[400]}
                                      />
                                    )}
                                  </td>
                                  <td style={tdStyle}>
                                    <RevenueCell revenue={flowRevenue(flow)} />
                                  </td>
                                  <td
                                    style={{
                                      ...tdStyle,
                                      paddingRight: 20,
                                      width: 1,
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 8,
                                        justifyContent: 'flex-end',
                                      }}
                                      onClick={stopRowClick}
                                      onKeyDown={(e) => e.stopPropagation()}
                                      role="presentation"
                                    >
                                      <RowActions
                                        visible={hovered}
                                        busy={bulkBusy}
                                        onView={() => viewRow(row)}
                                        onEdit={() => openFlow(flow)}
                                        onCopy={() => handleDuplicateOne(row)}
                                      />
                                    </div>
                                  </td>
                                </>
                              )}
                            </ListRow>
                          )
                        }

                        const c = row.campaign
                        const type = campaignType(c)
                        const tint = CAMPAIGN_TYPE_TINTS[type]
                        const displayStatus = displayStatusForCampaign(c)
                        const channel = campaignChannel(c)
                        const failedCount = c.stats?.failed ?? 0
                        const skippedCount = c.stats?.skipped ?? 0
                        const detail =
                          channel === 'sms' ? c.sms_body || '' : c.subject || ''
                        const detailPreview =
                          detail.length > 70
                            ? `${detail.slice(0, 70)}…`
                            : detail
                        const audience = campaignAudienceSummary(
                          c.audience,
                          segmentById,
                          c.stats?.recipients,
                        )
                        const performance = campaignPerformance(c)
                        const isSmart = isSmartSendCampaign(c)
                        const contentLocked =
                          !CONTENT_EDITABLE_STATUSES.includes(c.status)
                        return (
                          <ListRow
                            key={row.key}
                            onClick={() => viewRow(row)}
                            selected={selectedKeys.has(row.key)}
                            title={
                              contentLocked
                                ? `This campaign is ${displayStatus} — the builder opens read-only`
                                : undefined
                            }
                          >
                            {(hovered) => (
                              <>
                                <td
                                  style={{
                                    ...tdStyle,
                                    paddingLeft: 20,
                                    width: 1,
                                  }}
                                >
                                  <RowCheckbox
                                    checked={selectedKeys.has(row.key)}
                                    label={c.name || 'Campaign'}
                                    onToggle={() => toggleSelected(row.key)}
                                  />
                                </td>
                                <td style={tdStyle}>
                                  <CampaignCell
                                    icon={<CampaignTypeIcon type={type} />}
                                    tint={tint}
                                    name={c.name || '—'}
                                    description={detailPreview}
                                  />
                                </td>
                                <td style={tdStyle}>
                                  <TypeTag type={type} />
                                </td>
                                <td style={tdStyle}>
                                  <div
                                    style={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'flex-start',
                                      gap: 3,
                                    }}
                                  >
                                    <StatusPill status={displayStatus} />
                                    {c.status_note && (
                                      <span
                                        style={{
                                          fontSize: 10.5,
                                          fontWeight: 600,
                                          color: p.amber[700],
                                          maxWidth: 220,
                                          lineHeight: 1.4,
                                        }}
                                      >
                                        {c.status_note}
                                      </span>
                                    )}
                                    {/* A 'sent' campaign with failures/skips must
                                    not look like a clean completion — annotate
                                    under the pill. */}
                                    {(failedCount > 0 || skippedCount > 0) && (
                                      <span
                                        style={{
                                          fontSize: 10.5,
                                          fontWeight: 600,
                                          whiteSpace: 'nowrap',
                                        }}
                                      >
                                        {failedCount > 0 && (
                                          <span style={{ color: p.red[700] }}>
                                            {`${failedCount.toLocaleString()} failed`}
                                          </span>
                                        )}
                                        {failedCount > 0 &&
                                          skippedCount > 0 && (
                                            <span
                                              style={{ color: p.gray[400] }}
                                            >
                                              {' · '}
                                            </span>
                                          )}
                                        {skippedCount > 0 && (
                                          <span style={{ color: p.gray[400] }}>
                                            {`${skippedCount.toLocaleString()} skipped`}
                                          </span>
                                        )}
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td style={tdStyle}>
                                  <AudienceCell
                                    label={audience.label}
                                    size={audience.size}
                                  />
                                </td>
                                <td style={tdStyle}>
                                  <PerformanceCell
                                    performance={performance}
                                    stats={c.stats}
                                  />
                                </td>
                                <td style={tdStyle}>
                                  {displayStatus === 'draft' ? (
                                    <DateCell
                                      primary="Draft"
                                      sub="Not scheduled"
                                      color={p.gray[400]}
                                    />
                                  ) : c.status === 'scheduled' ||
                                    c.status === 'paused' ? (
                                    <DateCell
                                      primary={
                                        c.status === 'paused'
                                          ? 'Paused'
                                          : 'Scheduled'
                                      }
                                      // A smart campaign has no single
                                      // timestamp, so formatDate(undefined)
                                      // rendered '—'. Say what the drawer
                                      // says instead.
                                      sub={
                                        isSmart
                                          ? SMART_SEND_LABEL
                                          : formatDate(c.scheduled_at)
                                      }
                                    />
                                  ) : c.status === 'preparing' ||
                                    c.status === 'sending' ? (
                                    <DateCell
                                      primary={
                                        c.status === 'preparing'
                                          ? 'Preparing'
                                          : 'Active'
                                      }
                                      sub={
                                        isSmart
                                          ? SMART_SEND_LABEL
                                          : `Started ${formatDate(c.scheduled_at || c.created_at)}`
                                      }
                                      color={p.brand.primary}
                                    />
                                  ) : (
                                    <DateCell
                                      primary={
                                        c.status === 'failed'
                                          ? 'Failed'
                                          : 'Sent'
                                      }
                                      sub={formatDate(
                                        c.sent_at || c.scheduled_at,
                                      )}
                                    />
                                  )}
                                </td>
                                <td style={tdStyle}>
                                  <RevenueCell revenue={campaignRevenue(c)} />
                                </td>
                                <td
                                  style={{
                                    ...tdStyle,
                                    paddingRight: 20,
                                    width: 1,
                                  }}
                                >
                                  <div
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: 8,
                                      justifyContent: 'flex-end',
                                    }}
                                    onClick={stopRowClick}
                                    onKeyDown={(e) => e.stopPropagation()}
                                    role="presentation"
                                  >
                                    <RowActions
                                      visible={hovered}
                                      busy={bulkBusy}
                                      onView={() => viewRow(row)}
                                      onEdit={() => openCampaign(c)}
                                      onCopy={() => handleDuplicateOne(row)}
                                    />
                                  </div>
                                </td>
                              </>
                            )}
                          </ListRow>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <CampaignDetailDrawer
        storeId={storeId}
        target={drawerRecord}
        segments={segments ?? []}
        busy={
          bulkBusy ||
          updateMutation.isPending ||
          updateFlowMutation.isPending ||
          sendMutation.isPending
        }
        onClose={() => setDrawerTarget(null)}
        onEdit={editTarget}
        onDuplicate={(target) => handleDuplicateOne(targetToRow(target))}
        onPause={pauseTarget}
        onResume={resumeTarget}
        onSend={sendTarget}
        onSchedule={scheduleFromDrawer}
        onBackToDraft={backToDraftTarget}
        onDelete={deleteFromDrawer}
      />

      {scheduleTarget && (
        <ScheduleCampaignDialog
          open={scheduleTarget !== null}
          title={
            scheduleTarget.mode === 'resume'
              ? `Resume — ${scheduleTarget.campaign.name}`
              : `Schedule — ${scheduleTarget.campaign.name}`
          }
          confirmLabel={
            scheduleTarget.mode === 'resume' ? 'Resume' : 'Schedule'
          }
          initialValue={scheduleTarget.campaign.scheduled_at}
          isSubmitting={updateMutation.isPending}
          onClose={() => setScheduleTarget(null)}
          onConfirm={(scheduledAtIso) =>
            handleTransition(
              scheduleTarget.campaign,
              { status: 'scheduled', scheduled_at: scheduledAtIso },
              scheduleTarget.mode === 'resume'
                ? 'Campaign resumed'
                : 'Campaign scheduled',
            )
          }
        />
      )}

      <Modal
        open={deleteTarget !== null}
        onClose={
          deleteMutation.isPending || deleteFlowMutation.isPending
            ? () => {}
            : () => setDeleteTarget(null)
        }
        title={
          deleteTarget?.kind === 'flow' ? 'Delete flow' : 'Delete campaign'
        }
        width={400}
        zIndex={1500}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button
              variant="ghost"
              onClick={() => setDeleteTarget(null)}
              disabled={
                deleteMutation.isPending || deleteFlowMutation.isPending
              }
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDelete}
              disabled={
                deleteMutation.isPending || deleteFlowMutation.isPending
              }
            >
              {deleteMutation.isPending || deleteFlowMutation.isPending
                ? 'Deleting…'
                : 'Delete'}
            </Button>
          </div>
        }
      >
        <div style={{ fontSize: 13.5, color: p.gray[700] }}>
          Are you sure you want to delete{' '}
          <strong>
            “
            {deleteTarget?.kind === 'flow'
              ? deleteTarget.flow.name
              : deleteTarget?.campaign.name}
            ”
          </strong>
          ? This cannot be undone.
        </div>
      </Modal>

      <Modal
        open={bulkDeleteOpen}
        onClose={bulkBusy ? () => {} : () => setBulkDeleteOpen(false)}
        title="Delete selected"
        width={420}
        zIndex={1500}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button
              variant="ghost"
              onClick={() => setBulkDeleteOpen(false)}
              disabled={bulkBusy}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleBulkDelete}
              disabled={bulkBusy}
            >
              {bulkBusy ? 'Deleting…' : `Delete ${bulkDeletable.length}`}
            </Button>
          </div>
        }
      >
        <div style={{ fontSize: 13.5, color: p.gray[700] }}>
          Delete {bulkDeletable.length} selected{' '}
          {bulkDeletable.length === 1 ? 'item' : 'items'}? This cannot be
          undone.
          {bulkDeletable.length < selectedRows.length && (
            <div style={{ marginTop: 6, color: p.gray[500], fontSize: 12.5 }}>
              {bulkDeleteSkippedNote(
                selectedRows.length - bulkDeletable.length,
                selectedRows.some(
                  (row) =>
                    row.kind === 'campaign' &&
                    row.campaign.status === 'preparing',
                ),
              )}
            </div>
          )}
        </div>
      </Modal>

      {toastNode}
    </>
  )
}
